import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type ReactNode } from 'react'

import { useAuth } from '../AuthContext'
import { useSelectedHospital } from '../SelectedHospitalContext'
import { ApiError } from '../api/client'
import {
  APPOINTMENTS_KEY,
  completeAppointment,
  confirmAppointment,
  declineAppointment,
  fetchAppointments,
  type AdminAppointment,
} from '../api/appointmentRequests'
import { Callout } from '../components/Callout'
import { SEARCHABLE } from '../searchable'
import { useServerSearch } from '../TableFilterContext'
import { DataTable, type Column } from '../components/DataTable'
import { DetailDrawer, DrawerField } from '../components/DetailDrawer'
import { EmptyState } from '../components/EmptyState'
import { FilterTabs } from '../components/FilterTabs'
import { StatusPill } from '../components/StatusPill'
import { sessionLabel } from '../../lib/sessionLabel'

const FILTERS = [
  { key: null, label: 'All' },
  { key: 'requested', label: 'Requested' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'completed', label: 'Completed' },
  { key: 'declined', label: 'Declined' },
  { key: 'cancelled', label: 'Cancelled' },
] as const

function when(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  })}`
}

/**
 * Appointment queue (M14-T12). amtc_admin actions the queue (confirm/decline/
 * complete on a call back); a hospital_admin sees only their hospital's,
 * read-only — no action controls rendered at all. The patient phone is a tel:
 * link because the whole flow is "AMTC calls the patient".
 */
export function AppointmentsPage(): ReactNode {
  const { token, user } = useAuth()
  const { hospitalId } = useSelectedHospital()
  const [status, setStatus] = useState<string | null>(null)
  const [open, setOpen] = useState<AdminAppointment | null>(null)
  const isAmtc = user?.role === 'amtc_admin'

  // Server search (M24-T05): the term is part of the query key, so React Query
  // caches per term and retyping one is instant. `keepPreviousData` is
  // deliberately NOT used — a stale table under a new term is how someone acts
  // on the previous search's results.
  const { term, setResultCount } = useServerSearch(SEARCHABLE.appointments)
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: [...APPOINTMENTS_KEY, hospitalId, status, term],
    queryFn: () => fetchAppointments(token, hospitalId, status, term),
    enabled: Boolean(token),
  })

  const rows = data?.data ?? []
  useEffect(() => {
    setResultCount(data === undefined ? null : data.data.length)
  }, [data, setResultCount])
  // The drawer must follow the refetched row, not the snapshot it was opened
  // with — otherwise confirming leaves stale values on screen behind the toast.
  const openRow = open ? (rows.find((r) => r.reference === open.reference) ?? open) : null

  const columns: Column<AdminAppointment>[] = [
    {
      key: 'reference',
      header: 'Reference',
      cell: (a) => <span className="font-mono text-[13px]">{a.reference}</span>,
    },
    {
      key: 'patient',
      header: 'Patient',
      cell: (a) => (
        <>
          <span className="block text-ink-1">{a.patient_name ?? '—'}</span>
          {a.patient_phone ? (
            <a
              href={`tel:${a.patient_phone}`}
              className="block whitespace-nowrap text-xs font-medium text-accent-ink hover:underline"
            >
              {a.patient_phone}
            </a>
          ) : null}
        </>
      ),
    },
    {
      key: 'specialist',
      header: 'Specialist',
      cell: (a) => (
        <>
          <span className="block text-ink-1">{a.specialist_name}</span>
          <span className="block text-xs text-ink-3">
            {a.specialty} · {a.hospital_name}
          </span>
        </>
      ),
    },
    {
      key: 'requested',
      header: 'Requested for',
      cell: (a) => (
        <>
          <span className="block whitespace-nowrap text-ink-1">
            {a.requested_date}
          </span>
          <span className="block text-xs text-ink-3">{sessionLabel(a.session)}</span>
          {a.confirmed_for ? (
            <span className="block text-xs text-bed-good">
              confirmed {when(a.confirmed_for)}
            </span>
          ) : null}
        </>
      ),
    },
    { key: 'status', header: 'Status', cell: (a) => <StatusPill status={a.status} /> },
  ]

  if (isAmtc) {
    columns.push({
      key: 'action',
      header: 'Action',
      // DataTable already ignores row clicks that land on a control, so the
      // buttons here do not also open the drawer.
      cell: (a) => <ActionCell appointment={a} />,
    })
  }

  return (
    <section className="flex flex-col gap-5">
      <div>
        <h1 className="font-display text-2xl font-semibold text-ink-1">
          Appointments
        </h1>
        <p className="mt-1 text-sm text-ink-3">
          Specialist appointment requests. Newest first.
          {isAmtc
            ? ' Call the patient first, then confirm or decline.'
            : ' Read-only — the AMTC team confirms.'}
        </p>
      </div>

      {/* No counts: the API returns one filtered page at a time, so a bucket
          total would be a number this page cannot actually know. The frames'
          counts do not add up either (M23 defect D11). */}
      <FilterTabs tabs={FILTERS} active={status} onChange={setStatus} />

      <DataTable
        rows={rows}
        columns={columns}
        getKey={(a) => a.reference}
        searchText={(a) =>
          `${a.reference} ${a.patient_name ?? ''} ${a.patient_phone ?? ''} ${a.specialist_name} ${a.specialty}`
        }
        caption="Appointment requests"
        total={data?.total}
        isPending={isPending}
        isError={isError}
        {...(error instanceof ApiError && { errorMessage: error.message })}
        onRetry={() => void refetch()}
        onRowClick={(a) => setOpen(a)}
        empty={
          <EmptyState title="No appointment requests.">
            {status === null
              ? 'Requests appear here as patients send them.'
              : 'Nothing in this status. Try All.'}
          </EmptyState>
        }
      />

      <DetailDrawer
        open={openRow !== null}
        onClose={() => setOpen(null)}
        title="Appointment request"
      >
        {openRow !== null && (
          <>
            <dl>
              <DrawerField label="Patient">{openRow.patient_name ?? '—'}</DrawerField>
              <DrawerField label="Phone number">
                {openRow.patient_phone ? (
                  <a href={`tel:${openRow.patient_phone}`} className="hover:underline">
                    {openRow.patient_phone}
                  </a>
                ) : (
                  '—'
                )}
              </DrawerField>
              <DrawerField label="Specialist">{openRow.specialist_name}</DrawerField>
              <DrawerField label="Specialty">{openRow.specialty}</DrawerField>
              <DrawerField label="Hospital">{openRow.hospital_name}</DrawerField>
              <DrawerField label="Date requested">
                {openRow.requested_date} · {sessionLabel(openRow.session)}
              </DrawerField>
              {openRow.confirmed_for ? (
                <DrawerField label="Confirmed for">
                  {when(openRow.confirmed_for)}
                </DrawerField>
              ) : null}
              {openRow.decline_reason ? (
                <DrawerField label="Decline reason">
                  {openRow.decline_reason}
                </DrawerField>
              ) : null}
              <DrawerField label="Reason for visit">
                {openRow.reason_for_visit}
              </DrawerField>
              <DrawerField label="Status">
                <StatusPill status={openRow.status} />
              </DrawerField>
            </dl>

            {isAmtc && openRow.status === 'requested' && (
              <div className="mt-6">
                <Callout>
                  Call the patient before confirming. Confirming sends them the
                  slot and the hospital address.
                </Callout>
              </div>
            )}

            {isAmtc && (
              <div className="mt-6">
                {/* The same component the row renders — one code path, so the
                    drawer cannot drift from the table. */}
                <ActionCell appointment={openRow} />
              </div>
            )}
          </>
        )}
      </DetailDrawer>
    </section>
  )
}

/** amtc_admin action controls for one row. Confirm sets a date/time (pre-filled
 * with the requested date but editable — AMTC's phone agreement overrides the
 * pattern). Decline needs a reason. Complete is offered on confirmed rows. */
function ActionCell({ appointment: a }: { appointment: AdminAppointment }): ReactNode {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  const [mode, setMode] = useState<'idle' | 'confirm' | 'decline'>('idle')
  const [confirmedFor, setConfirmedFor] = useState(`${a.requested_date}T09:00`)
  const [reason, setReason] = useState('')

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: APPOINTMENTS_KEY })

  const confirmMut = useMutation({
    mutationFn: () =>
      confirmAppointment(token, a.reference, new Date(confirmedFor).toISOString()),
    onSuccess: () => {
      setMode('idle')
      invalidate()
    },
  })
  const declineMut = useMutation({
    mutationFn: () => declineAppointment(token, a.reference, reason.trim()),
    onSuccess: () => {
      setMode('idle')
      invalidate()
    },
  })
  const completeMut = useMutation({
    mutationFn: () => completeAppointment(token, a.reference),
    onSuccess: invalidate,
  })

  const err = confirmMut.error ?? declineMut.error ?? completeMut.error

  if (a.status === 'confirmed') {
    return (
      <div className="flex flex-col gap-1">
        <button
          type="button"
          disabled={completeMut.isPending}
          onClick={() => completeMut.mutate()}
          className="rounded-[8px] border border-line-2 px-2.5 py-1 text-[12px] font-medium text-ink-1 hover:bg-bg-soft disabled:opacity-50"
        >
          {completeMut.isPending ? 'Completing…' : 'Mark completed'}
        </button>
        {err ? <ActionError err={err} /> : null}
      </div>
    )
  }

  if (a.status !== 'requested') {
    return <span className="text-[12px] text-ink-3">—</span>
  }

  if (mode === 'confirm') {
    return (
      <div className="flex flex-col gap-1.5">
        <label className="text-[11px] text-ink-3">
          Confirmed time
          <input
            type="datetime-local"
            value={confirmedFor}
            onChange={(e) => setConfirmedFor(e.target.value)}
            className="mt-0.5 block rounded-[8px] border border-line-2 bg-bg-app px-2 py-1 text-[12px]"
          />
        </label>
        <div className="flex gap-1.5">
          <button
            type="button"
            disabled={confirmMut.isPending}
            onClick={() => confirmMut.mutate()}
            className="rounded-[8px] bg-accent px-2.5 py-1 text-[12px] font-medium text-white disabled:opacity-50"
          >
            {confirmMut.isPending ? 'Confirming…' : 'Confirm'}
          </button>
          <button
            type="button"
            onClick={() => setMode('idle')}
            className="rounded-[8px] border border-line-2 px-2.5 py-1 text-[12px] text-ink-1 hover:bg-bg-soft"
          >
            Cancel
          </button>
        </div>
        {err ? <ActionError err={err} /> : null}
      </div>
    )
  }

  if (mode === 'decline') {
    return (
      <div className="flex flex-col gap-1.5">
        <input
          type="text"
          aria-label={`Decline reason for ${a.reference}`}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason…"
          className="rounded-[8px] border border-line-2 bg-bg-app px-2 py-1 text-[12px]"
        />
        <div className="flex gap-1.5">
          <button
            type="button"
            disabled={reason.trim().length === 0 || declineMut.isPending}
            onClick={() => declineMut.mutate()}
            className="rounded-[8px] bg-bed-bad px-2.5 py-1 text-[12px] font-medium text-white disabled:opacity-50"
          >
            {declineMut.isPending ? 'Declining…' : 'Decline'}
          </button>
          <button
            type="button"
            onClick={() => setMode('idle')}
            className="rounded-[8px] border border-line-2 px-2.5 py-1 text-[12px] text-ink-1 hover:bg-bg-soft"
          >
            Cancel
          </button>
        </div>
        {err ? <ActionError err={err} /> : null}
      </div>
    )
  }

  return (
    <div className="flex gap-1.5">
      <button
        type="button"
        onClick={() => setMode('confirm')}
        className="rounded-[8px] bg-accent px-2.5 py-1 text-[12px] font-medium text-white"
      >
        Confirm
      </button>
      <button
        type="button"
        onClick={() => setMode('decline')}
        className="rounded-[8px] border border-line-2 px-2.5 py-1 text-[12px] text-ink-1 hover:bg-bg-soft"
      >
        Decline
      </button>
    </div>
  )
}

function ActionError({ err }: { err: unknown }): ReactNode {
  return (
    <p role="alert" className="text-[11px] text-bed-bad">
      {err instanceof ApiError ? err.message : 'Action failed.'}
    </p>
  )
}
